import React, { useCallback, useState } from 'react'
import { NavLink } from 'react-router-dom'
import styled from 'styled-components'

import LegacyButton from 'components/LegacyButton'
import { Button } from '@nexiahome/ui-common/components/primitives/buttons'
import Modal from 'components/Modal'
import ProductLiteratureSearchModal from 'pages/Device/SystemComponentsPanel/ProductLiteratureSearchModal'
import Table from 'components/Table'
import Tooltip from 'components/Tooltip'
import Spinner from 'components/Spinner'

import WarrantyInfo from 'pages/shared/WarrantyInfo'

import { shortDate } from 'helpers/formatters'

import useEquipmentImage from 'hooks/useEquipmentImage'
import useWarrantyInfo from 'hooks/useWarrantyInfo'
import { useSessionContext } from 'hooks/SessionContext'
import { useStoreContext } from 'hooks/StoreContext'

import { SystemComponent } from 'types/SystemComponent'

import { color } from 'styles/colors'

import { screenSize } from 'styles/shared'

import { renderWarrantyStatus } from './helpers/renderWarrantyStatus'
import RenderFirmwareStatus from 'pages/shared/RenderFirmwareStatus'

export type Props = {
  content: Required<SystemComponent>
  disabled: boolean
  onEditCallBack: () => void
  showSpinner: boolean
}

const DisplayComponent = ({ content, disabled, onEditCallBack, showSpinner = false }: Props) => {
  const { session } = useSessionContext()
  const { warrantyInfo, isLoaded: isWarrantyInfoLoaded, hasError: warrantyInfoCallFailed } = useWarrantyInfo(content.serialNumber, true)
  const { equipmentImage, isLoaded: isEquipmentImageLoaded } = useEquipmentImage(content.modelNumber)

  const [showProductLiteratureModal, setShowProductLiteratureModal] = useState(false)
  const [modalOpen, setModalOpen] = useState(false)

  const { defaultFirmwareLoads, areDefaultFirmwareLoadsLoaded } = useStoreContext()

  const communicatingStatus = () => {
    if (content.isConnected != null) {
      return content.isConnected ? 'Online' : 'Offline'
    } else {
      <Button decoration="secondary" text="Edit" onPress={onEditCallBack} />

      return (!disabled && !(session.isFsr || session.isTechnician)) ? <Button decoration="secondary" text="Edit" onPress={onEditCallBack} showSpinner={showSpinner} /> : <></>
    }
  }

  const getWarrantyExpiration = useCallback(() => {
    switch (warrantyInfo?.policyStatus?.toLowerCase() || null) {
      case 'active':
        return 'until ' + shortDate(warrantyInfo?.endDate)
      default:
        return 'on ' + shortDate(warrantyInfo?.endDate)
    }
  }, [warrantyInfo?.policyStatus, warrantyInfo?.endDate])

  const getEquipmentImage = equipmentImage?.url ? <S.Image src={equipmentImage.url} alt="equipment" /> : <></>

  return (
    <>
      <S.Cell>
        {
          isEquipmentImageLoaded
            ? getEquipmentImage
            : <S.ImageSpinner variant="cell" aria-label="equipment" />
        }
        <div>
          <S.Description>{content.description}</S.Description>
          {content.batteryLevel && <S.Battery>Battery {content.batteryLevel}%</S.Battery>}
        </div>
      </S.Cell>

      <S.Cell>
        <Tooltip title="Click model number for product literature">
          <NavLink
            to={''}
            onClick={(event) => {
              event.stopPropagation()
              setShowProductLiteratureModal(true)
            }}
          >
            {content.modelNumber}
          </NavLink>
        </Tooltip>
      </S.Cell>

      <S.Cell>{content.serialNumber}</S.Cell>
      <S.Cell>
        {content.versionNumber && content.modelNumber && (
          <RenderFirmwareStatus
            modelNumber={content.modelNumber}
            currentVersion={content.versionNumber}
            defaultFirmwareLoads={defaultFirmwareLoads}
            areDefaultFirmwareLoadsLoaded={areDefaultFirmwareLoadsLoaded}
            component="display-component"
          />
        )}
      </S.Cell>
      <S.Cell>{communicatingStatus()}</S.Cell>
      <S.Cell>
        {!isWarrantyInfoLoaded && <Spinner variant="cell" aria-label="warranty" />}
        {isWarrantyInfoLoaded && !warrantyInfo && renderWarrantyStatus(null, warrantyInfoCallFailed)}
        {
          isWarrantyInfoLoaded && warrantyInfo &&
          <S.WarrantyDetails>
            <S.WarrantyInfoButton variant="tertiary" onClick={() => setModalOpen(true)}>
              {renderWarrantyStatus(warrantyInfo?.policyStatus, warrantyInfoCallFailed)}
            </S.WarrantyInfoButton>
            <S.Expiration>
              {getWarrantyExpiration()}
            </S.Expiration>
          </S.WarrantyDetails>
        }
      </S.Cell>
      {
        isWarrantyInfoLoaded && warrantyInfo &&
          <Modal
            title={content.description}
            closeCallback={() => setModalOpen(false)}
            isDisplayed={modalOpen}
            variant={false}
            fullWidth={false}
          >
            <WarrantyInfo serialNumber={content.serialNumber} warrantyInfo={warrantyInfo} />
          </Modal>
      }

      <ProductLiteratureSearchModal
        closeCallback={() => setShowProductLiteratureModal(false)}
        modelNumber={content.modelNumber}
        isDisplayed={showProductLiteratureModal}
      />
    </>
  )
}

export default DisplayComponent

const S = {
  Image: styled.img`
    margin-right: 3px;
    max-height: 28px;
    max-width: 28px;

    @media ${screenSize.small} {
      margin-left: -5px;
    }
  `,
  ImageSpinner: styled(Spinner)`
    margin-right: 10px;
  `,
  Description: styled.span`
    font-weight: bold;
  `,
  Battery: styled.span`
    color: ${color('secondaryText')};
    margin-left: 7px;
    text-wrap: nowrap;

    display: block;
    margin-left: 0;
    font-size: 14px;
  `,
  Cell: styled(Table.Cell)`
    display: flex;
    padding: 10px 0 0 10px;
    align-items: center;
    vertical-align: middle;

    @media ${screenSize.small}, ${screenSize.medium} {
      padding: 0 0 0 15px;
      align-items: center;
    }
  `,
  Active: styled.span`
    color: ${color('positiveStatus')};
    font-weight: bold;
  `,
  NotFound: styled.span`
    color: ${color('noDataText')};
  `,
  Error: styled.span`
    color: ${color('noDataText'/* TODO: Per Mindy's request: Change color to 'negativeStatus' after we verify warranty info is working in prod */)};
  `,
  WarrantyInfoButton: styled(LegacyButton)`
    margin-right: 10px;
    color: ${color('warrantyText')};
    border: 1px solid ${color('border')};
    text-wrap: nowrap;
    padding: 0.75em 0.5em;
    &:hover {
      background: ${color('tertiaryButtonHover')};
      border: 1px solid ${color('border')};
      color: ${color('warrantyText')};
    }

    @media ${screenSize.small}, ${screenSize.medium} {
      margin: 10px 10px 10px 0;
    }
  `,
  Expiration: styled.span`
    text-wrap: nowrap;

    @media ${screenSize.small}, ${screenSize.medium} {
      font-size: 14px;
    }
  `,
  WarrantyDetails: styled.div`
    display: flex;
    align-items: center;

    @media ${screenSize.small}, ${screenSize.medium} {
      flex-direction: row;
      align-items: center;
    }
  `
}
